import React from 'react';
import { MRT_ColumnDef } from 'mantine-react-table';
import { formatDate } from 'src/utils';
import { Currency } from 'src/design/atoms/Currency';

export interface IChangeAmountRow {
  date?: Date;
  description?: string;
  proposedAmount?: number;
  approvedAmount?: number;
  proposedDays?: number;
  approvedDays?: number;
}

/**
 * @registry The shared Date/Summary/amount/day columns for change-order tables (job Changes list and the cross-job Changes overview). Returns MRT column defs for any row carrying the change money/date fields, including footer totals for the amount columns. Spread it into a table's column list alongside the table-specific columns (Change number, Status, people).
 */
export function changeAmountColumns<
  T extends IChangeAmountRow,
>(): MRT_ColumnDef<T>[] {
  return [
    {
      id: 'date',
      accessorFn: (row) => (row.date ? formatDate(row.date) : '--'),
      header: 'Date',
      size: 150,
    },
    {
      accessorKey: 'description',
      header: 'Summary',
      size: 200,
    },
    {
      accessorKey: 'proposedAmount',
      header: 'Proposed Amount',
      size: 160,
      Cell: ({ row }) => (
        <Currency value={row.original.proposedAmount} placeholder="--" />
      ),
      Footer: ({ table }) => {
        const total = table
          .getFilteredRowModel()
          .rows.reduce(
            (sum, row) => sum + (row.original.proposedAmount ?? 0),
            0,
          );
        return <Currency value={total} />;
      },
    },
    {
      accessorKey: 'approvedAmount',
      header: 'Approved Amount',
      size: 160,
      Cell: ({ row }) => (
        <Currency value={row.original.approvedAmount} placeholder="--" />
      ),
      Footer: ({ table }) => {
        const total = table
          .getFilteredRowModel()
          .rows.reduce(
            (sum, row) => sum + (row.original.approvedAmount ?? 0),
            0,
          );
        return <Currency value={total} />;
      },
    },
    {
      accessorKey: 'proposedDays',
      header: 'Proposed Days',
      size: 140,
      Cell: ({ row }) =>
        row.original.proposedDays == null
          ? '--'
          : `${row.original.proposedDays} days`,
    },
    {
      accessorKey: 'approvedDays',
      header: 'Approved Days',
      size: 140,
      Cell: ({ row }) =>
        row.original.approvedDays == null
          ? '--'
          : `${row.original.approvedDays} days`,
    },
  ];
}

/**
 * @registry The shared Date/Summary/Proposed Amount/Approved Amount/Proposed Days/Approved Days CSV export cells for change-order tables, with raw numbers for the amount and day columns. Spread the result into each table's row object alongside its table-specific columns (Job, Change, Status, people).
 */
export function changeAmountExportRow(
  item: IChangeAmountRow,
): Record<string, string | number> {
  return {
    ['Date']: item.date ? formatDate(item.date) : '--',
    ['Summary']: item.description ?? '',
    ['Proposed Amount']: item.proposedAmount ?? 0,
    ['Approved Amount']: item.approvedAmount ?? 0,
    ['Proposed Days']: item.proposedDays ?? 0,
    ['Approved Days']: item.approvedDays ?? 0,
  };
}
